import React from 'react';

import PropTypes from 'prop-types';

import * as analytics from '@adsk/acs-web-analytics';
import { useUser } from '@adsk/alloy-provider';
import { StylableComponent, stylePropType } from '@adsk/alloy-react-helpers';
import theme from '@adsk/alloy-react-theme';

import {
  MentionData,
  MentionType,
  MENTION_ANALYTICS,
  MENTION_TYPES,
} from '../consts';
import { MentionCompanyTooltip } from './MentionTooltips/MentionCompanyTooltip';
import { MentionRoleTooltip } from './MentionTooltips/MentionRoleTooltip';
import { MentionUserTooltip } from './MentionTooltips/MentionUserTooltip';

export type CalloutMentionProps = StylableComponent<HTMLSpanElement> & {
  entityKey?: string;
  decoratedText?: string;
  mention?: MentionData;
  renderTooltip?: ({ suggestion }: { suggestion?: MentionData }) => JSX.Element;
};

const CALLOUT_TOOLTIPS = {
  [MENTION_TYPES.USER]: MentionUserTooltip,
  [MENTION_TYPES.COMPANY]: MentionCompanyTooltip,
  [MENTION_TYPES.ROLE]: MentionRoleTooltip,
};

function renderDefaultTooltip({
  mention,
  children,
}: {
  mention?: MentionData;
  children?: React.ReactNode;
}) {
  const TooltipComponent =
    CALLOUT_TOOLTIPS[mention?.type?.toLocaleLowerCase() as MentionType];

  if (!mention) {
    return null;
  }

  if (!TooltipComponent) {
    const error = `renderDefaultTooltip: No mention tooltip for type ${mention?.type}`;

    console.error(error);
    analytics.track(MENTION_ANALYTICS.ERROR, { error });

    return null;
  }

  return <TooltipComponent mention={mention}>{children}</TooltipComponent>;
}

const MentionCallout = React.forwardRef<HTMLSpanElement, CalloutMentionProps>(
  (
    {
      className,
      style,
      mention,
      children,
      entityKey,
      decoratedText,
      onClick,
      onMouseEnter,
      renderTooltip = renderDefaultTooltip,
      ...props
    },
    ref
  ) => {
    const { user: currentUser } = useUser();

    return (
      <span
        ref={ref}
        className={className}
        css={[
          theme.typography.bodyMediumBold,
          {
            display: 'inline-block',
            cursor: 'pointer',
            background: theme.colors.charcoal100,
            color: theme.colors.charcoal900,
            '&:hover': {
              background: theme.colors.charcoal200,
            },

            ...(mention?.type === MENTION_TYPES.USER &&
              currentUser?.autodeskId === mention?.id && {
                background: theme.colors.yellowOrange100,
                '&:hover': {
                  background: theme.colors.yellowOrange300,
                },
              }),
          },
          style,
        ]}
        onClick={(e) => {
          if (!children) {
            // report view only
            analytics.track(MENTION_ANALYTICS.VIEW_CLICK, {
              mention_type: mention?.type,
              mention_name: mention?.name,
            });
          }

          onClick?.(e);
        }}
        onMouseEnter={(e) => {
          if (!children) {
            // report view only
            analytics.track(MENTION_ANALYTICS.VIEW_HOVER, {
              mention_type: mention?.type,
              mention_name: mention?.name,
            });
          }

          onMouseEnter?.(e);
        }}
        {...props}
      >
        {renderTooltip({ mention, children })}
      </span>
    );
  }
);

MentionCallout.displayName = 'MentionCallout';

MentionCallout.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** used for mention block that editor passing, used for mention deletion */
  children: PropTypes.array,
  /** mention data parsed from text */
  mention: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    type: PropTypes.oneOf(Object.values(MENTION_TYPES)).isRequired,
  }).isRequired,
  /** render tooltip allows overwrite tooltip */
  renderTooltip: PropTypes.func,
  /** triggered when callout onClick event fired */
  onClick: PropTypes.func,
  /** triggered when callout onMouseEnter event fired */
  onMouseEnter: PropTypes.func,
};

export default MentionCallout;
